import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Card, ProgressBar, Button, Form } from 'react-bootstrap';
import SidebarAdmin from './SidebarAdmin';

// Définition de 30 jours en millisecondes (30j * 24h * 60m * 60s * 1000ms)
const TRENTE_JOURS_EN_MS = 30 * 24 * 60 * 60 * 1000;
const TIMER_ID = "outil_temporel_30j";

export default function OutilsTemporels() {
  const [estActif, setEstActif] = useState(() => {
    const sauvegardeActif = localStorage.getItem(`minuteur_active_${TIMER_ID}`);
    return sauvegardeActif === 'true';
  });

  const [tempsRestant, setTempsRestant] = useState(() => {
    const sauvegardeTemps = localStorage.getItem(`minuteur_timer_${TIMER_ID}`);
    const sauvegardeDate = localStorage.getItem(`minuteur_timestamp_${TIMER_ID}`);
    const initialTemps = sauvegardeTemps ? parseInt(sauvegardeTemps, 10) : TRENTE_JOURS_EN_MS;

    const sauvegardeActif = localStorage.getItem(`minuteur_active_${TIMER_ID}`) === 'true';
    if (sauvegardeActif && sauvegardeDate) {
      const tempsEcoulePendantAbsence = Date.now() - parseInt(sauvegardeDate, 10);
      const tempsAjuste = initialTemps - tempsEcoulePendantAbsence;
      return tempsAjuste > 0 ? tempsAjuste : 0;
    }

    return initialTemps;
  });

  const [modeEdition, setModeEdition] = useState(false);
  const [editJ, setEditJ] = useState(30);
  const [editH, setEditH] = useState(0);
  const [editM, setEditM] = useState(0);
  const [editS, setEditS] = useState(0);

  const intervalleRef = useRef(null);

  // Sauvegarde automatique en temps réel
  useEffect(() => {
    localStorage.setItem(`minuteur_active_${TIMER_ID}`, estActif.toString());
    localStorage.setItem(`minuteur_timer_${TIMER_ID}`, tempsRestant.toString());
    localStorage.setItem(`minuteur_timestamp_${TIMER_ID}`, Date.now().toString());
  }, [tempsRestant, estActif]);

  // Boucle interne du compte à rebours
  useEffect(() => {
    if (estActif && !modeEdition) {
      intervalleRef.current = setInterval(() => {
        setTempsRestant((prev) => {
          if (prev <= 1000) {
            clearInterval(intervalleRef.current);
            setEstActif(false);
            return 0;
          }
          return prev - 1000;
        });
      }, 1000);
    } else {
      clearInterval(intervalleRef.current);
    }
    return () => clearInterval(intervalleRef.current);
  }, [estActif, modeEdition]);

  // Synchronisation automatique des champs pour l'édition au clavier
  useEffect(() => {
    if (!modeEdition) {
      setEditJ(Math.floor(tempsRestant / (1000 * 60 * 60 * 24)));
      setEditH(Math.floor((tempsRestant / (1000 * 60 * 60)) % 24));
      setEditM(Math.floor((tempsRestant / 1000 / 60) % 60));
      setEditS(Math.floor((tempsRestant / 1000) % 60));
    }
  }, [tempsRestant, modeEdition]);

  const appliquerChangements = () => {
    const totalMs =
      (Math.max(0, editJ) * 24 * 60 * 60 * 1000) +
      (Math.min(23, Math.max(0, editH)) * 60 * 60 * 1000) +
      (Math.min(59, Math.max(0, editM)) * 60 * 1000) +
      (Math.min(59, Math.max(0, editS)) * 1000);

    setTempsRestant(totalMs);
    setModeEdition(false);
  };

  const j = Math.floor(tempsRestant / (1000 * 60 * 60 * 24));
  const h = Math.floor((tempsRestant / (1000 * 60 * 60)) % 24);
  const m = Math.floor((tempsRestant / 1000 / 60) % 60);
  const s = Math.floor((tempsRestant / 1000) % 60);
  const pourcentage = ((TRENTE_JOURS_EN_MS - tempsRestant) / TRENTE_JOURS_EN_MS) * 100;

  return (
    <Container className="py-5">
      <SidebarAdmin />
      <div className="admin-main-wrapper w-100" style={{ marginBottom: '80px' }}>
      <h1 className="text-center text-white fw-bold mb-4 display-4 text-uppercase" style={{ letterSpacing: '2px' }}>
        Minu<span className="text-warning">teur</span>
      </h1>

      <Row className="justify-content-center">
        {/* On lui donne une largeur plus importante (6 colonnes sur grand écran) car c'est un compteur 30 jours massif */}
        <Col xs={12} md={8} lg={6}>
          <Card variant="dark" className="bg-dark border-secondary text-white shadow-lg">
            <Card.Body className="d-flex flex-column justify-content-between p-4">

              {/* En-tête de la carte avec l'icône Bouclier */}
              <div className="d-flex justify-content-between align-items-center mb-4">
                <Card.Title className="fs-5 fw-bold mb-0 d-flex align-items-center gap-2 text-primary">
                  <i className="material-icons d-flex align-items-center">hourglass_full</i>
                  <span>Pomodoro</span>
                </Card.Title>

                {modeEdition ? (
                  <Button variant="success" size="sm" onClick={appliquerChangements} className="py-1 px-3 d-flex align-items-center gap-1 text-dark fw-bold">
                    <i className="material-icons fs-6">check</i> Valider
                  </Button>
                ) : (
                    <Button variant="outline-primary" size="sm" onClick={() => { setEstActif(false); setModeEdition(true); }} disabled={estActif} className="py-1 px-3 d-flex align-items-center gap-1">
                      <i className="material-icons fs-6">edit</i> Configurer
                  </Button>
                  )}
              </div>

              {/* Affichage / Inputs des Chiffres */}
              <div className="d-flex justify-content-around align-items-center text-center my-4 bg-black bg-opacity-30 py-3 rounded border border-secondary border-opacity-20">
                {/* Jours */}
                <div>
                  {modeEdition ? (
                    <Form.Control
                      type="number"
                      min="0"
                      value={editJ}
                      onChange={(e) => setEditJ(Math.max(0, parseInt(e.target.value) || 0))}
                      className="bg-secondary text-center fw-bold fs-2 text-white p-1 border-0 rounded hide-arrows"
                      style={{ width: '65px' }}
                    />
                  ) : (
                      <div className="fs-2 fw-bold text-white p-1">{j}</div>
                    )}
                  <small className="text-light fs-7 d-block mt-1 text-uppercase">Jours</small>
                </div>

                <div className="fs-2 fw-bold text-secondary mb-3">:</div>

                {/* Heures */}
                <div>
                  {modeEdition ? (
                    <Form.Control
                      type="number"
                      min="0"
                      max="23"
                      value={editH}
                      onChange={(e) => setEditH(Math.min(23, Math.max(0, parseInt(e.target.value) || 0)))}
                      className="bg-secondary text-center fw-bold fs-2 text-primary p-1 border-0 rounded hide-arrows"
                      style={{ width: '60px' }}
                    />
                  ) : (
                      <div className="fs-2 fw-bold text-primary p-1">{String(h).padStart(2, '0')}</div>
                    )}
                  <small className="text-light fs-7 d-block mt-1 text-uppercase">Heures</small>
                </div>

                <div className="fs-2 fw-bold text-secondary mb-3">:</div>

                {/* Minutes */}
                <div>
                  {modeEdition ? (
                    <Form.Control
                      type="number"
                      min="0"
                      max="59"
                      value={editM}
                      onChange={(e) => setEditM(Math.min(59, Math.max(0, parseInt(e.target.value) || 0)))}
                      className="bg-secondary text-center fw-bold fs-2 text-primary p-1 border-0 rounded hide-arrows"
                      style={{ width: '60px' }}
                    />
                  ) : (
                      <div className="fs-2 fw-bold text-primary p-1">{String(m).padStart(2, '0')}</div>
                    )}
                  <small className="text-light fs-7 d-block mt-1 text-uppercase">Min</small>
                </div>

                <div className="fs-2 fw-bold text-secondary mb-3">:</div>

                {/* Secondes */}
                <div>
                  {modeEdition ? (
                    <Form.Control
                      type="number"
                      min="0"
                      max="59"
                      value={editS}
                      onChange={(e) => setEditS(Math.min(59, Math.max(0, parseInt(e.target.value) || 0)))}
                      className="bg-secondary text-center fw-bold fs-2 text-warning p-1 border-0 rounded hide-arrows"
                      style={{ width: '60px' }}
                    />
                  ) : (
                      <div className="fs-2 fw-bold text-warning p-1">{String(s).padStart(2, '0')}</div>
                    )}
                  <small className="text-light fs-7 d-block mt-1 text-uppercase">Sec</small>
                </div>
              </div>

              {/* Barre de Progression */}
              <div className="mb-4">
                <div className="d-flex justify-content-between small text-light mb-1">
                  <span>Temps écoulé</span>
                  <span>{Math.max(0, Math.min(100, Math.round(pourcentage)))}%</span>
                </div>
                <ProgressBar
                  variant="primary"
                  now={pourcentage}
                  className="bg-secondary"
                  style={{ height: '8px' }}
                />
              </div>

              {/* Boutons d'actions principaux */}
              <div className="d-flex gap-3 justify-content-center pt-3 border-top border-secondary border-opacity-20 button-minuteur">
                <Button
                  variant="success"
                  onClick={() => setEstActif(true)}
                  disabled={estActif || tempsRestant === 0 || modeEdition}
                  className="d-flex align-items-center gap-2 fw-semibold py-2 px-4 shadow-sm"
                >
                  <i className="material-icons">play_arrow</i> Lancer
                </Button>

                <Button
                  variant="warning"
                  onClick={() => setEstActif(false)}
                  disabled={!estActif || modeEdition}
                  className="d-flex align-items-center gap-2 fw-semibold text-dark py-2 px-4 shadow-sm"
                >
                  <i className="material-icons">pause</i> Interrompre
                </Button>

                <Button
                  variant="outline-secondary"
                  onClick={() => { setEstActif(false); setModeEdition(false); setTempsRestant(TRENTE_JOURS_EN_MS); }}
                  disabled={modeEdition}
                  className="d-flex align-items-center gap-1 text-white py-2 px-3"
                >
                  <i className="material-icons">refresh</i> Réinitialiser
                </Button>
              </div>

            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Style CSS pour masquer les flèches des inputs numériques */}
      <style>{`
        .hide-arrows::-webkit-outer-spin-button,
        .hide-arrows::-webkit-inner-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
        .hide-arrows {
          -moz-appearance: textfield;
        }
      `}</style>
      </div>
    </Container>
  );
}
